import React, { useState, useEffect, useRef } from "react";
import { Check, Clock, Trash2, X, Search, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { APPOINTMENT_COLORS, COLOR_KEYS, DEFAULT_MULTIDAY_COLOR, DEFAULT_SINGLEDAY_COLOR, getAppointmentColor } from "@/utils/calendarConstants";
import { getLocalDateStr } from "@/utils/calendarUtils";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AppointmentModal({ isOpen, onClose, onSave, onDelete, initialData, selectedDate, selectedTime, checkOverlap, calendars, primaryCalendar, onToggleDone, isToday, prefilledContactRef, hideMultiDay = false }) {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isMultiDay, setIsMultiDay] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [priority, setPriority] = useState(5);
  const [color, setColor] = useState(''); // '' means use default
  const [note, setNote] = useState(''); // Notes field
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [overlapError, setOverlapError] = useState('');
  const [targetCalendar, setTargetCalendar] = useState(primaryCalendar || '');
  const [notesFocused, setNotesFocused] = useState(false);
  // Kontakt-Verknüpfung
  const [contactRef, setContactRef] = useState(null);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [contactSearchOpen, setContactSearchOpen] = useState(false);
  const contactSearchRef = useRef(null);

  // Get current color for display
  const currentColorKey = color || (isMultiDay ? DEFAULT_MULTIDAY_COLOR : DEFAULT_SINGLEDAY_COLOR);
  const currentColorData = APPOINTMENT_COLORS[currentColorKey];

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      setDate(initialData.date || '');
      setEndDate(initialData.endDate || initialData.date || '');
      setIsMultiDay(!!initialData.endDate && initialData.endDate !== initialData.date);
      setStartTime(initialData.startTime || '09:00');
      setEndTime(initialData.endTime || '10:00');
      setPriority(initialData.priority || 5);
      setColor(initialData.color || '');
      setNote(initialData.note || '');
      setTargetCalendar(initialData.exportedToCalendar || primaryCalendar || '');
      setContactRef(initialData.contactRef || null);
      setContactSearch(initialData.contactRef?.name || '');
    } else if (selectedDate) {
      setDate(selectedDate);
      setEndDate(selectedDate);
      setIsMultiDay(false);
      setTitle('');
      setPriority(5);
      setColor(''); // Use default based on type
      setNote('');
      const start = selectedTime || '09:00';
      const startHour = parseInt(start.split(':')[0]);
      const endHour = Math.min(startHour + 1, 23);
      setStartTime(start);
      setEndTime(`${endHour.toString().padStart(2, '0')}:00`);
      setTargetCalendar(primaryCalendar || '');
      // Use prefilledContactRef if provided (e.g. when opening from a contact's task)
      setContactRef(prefilledContactRef || null);
      setContactSearch(prefilledContactRef?.name || '');
    }
    setShowDeleteConfirm(false);
    setOverlapError('');
  }, [initialData, selectedDate, selectedTime, isOpen, primaryCalendar, prefilledContactRef]);

  // Live Kontakt-Suche
  useEffect(() => {
    if (!isOpen || !contactSearch.trim() || contactRef) { if (!contactRef) setContactResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/api/teampartner?typ=`);
        const q = contactSearch.toLowerCase();
        setContactResults((res.data || []).filter(c => c.name?.toLowerCase().includes(q)).slice(0, 6));
      } catch { setContactResults([]); }
    }, 200);
    return () => clearTimeout(timer);
  }, [contactSearch, isOpen, contactRef]);

  // Click-outside für Kontakt-Suche
  useEffect(() => {
    const handleClick = (e) => { if (contactSearchRef.current && !contactSearchRef.current.contains(e.target)) setContactSearchOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // When start time changes, ensure end time is not before it
  const handleStartTimeChange = (newStartTime) => {
    setStartTime(newStartTime);
    // Skip validation for multi-day events
    if (isMultiDay) return;
    // If end time is before new start time, adjust it (only for single-day)
    if (endTime <= newStartTime) {
      const startHour = parseInt(newStartTime.split(':')[0]);
      const endHour = Math.min(startHour + 1, 23);
      setEndTime(`${endHour.toString().padStart(2, '0')}:00`);
    }
  };

  // Get minimum end time (must be at least 15 minutes after start time) - only for single-day
  const getMinEndTime = () => {
    if (isMultiDay) return null; // No minimum for multi-day
    const [hour, min] = startTime.split(':').map(Number);
    const totalMin = min + 15;
    const minHour = totalMin >= 60 ? hour + 1 : hour;
    const minMin = totalMin >= 60 ? totalMin - 60 : totalMin;
    return `${minHour.toString().padStart(2, '0')}:${minMin.toString().padStart(2, '0')}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !date || !startTime || !endTime) return;
    // Ensure end time is after start time (for single day events)
    if (!isMultiDay && endTime <= startTime) return;
    // For multi-day events, ensure end date is not before start date
    if (isMultiDay && endDate < date) return;
    
    // Check for overlaps before saving (only for single day events)
    if (!isMultiDay) {
      const overlap = checkOverlap ? checkOverlap(date, startTime, endTime, initialData?.id) : null;
      if (overlap) {
        setOverlapError(`Überschneidung mit "${overlap.title}" (${overlap.startTime}-${overlap.endTime})`);
        return;
      }
    }
    
    onSave({
      id: initialData?.id || Date.now(),
      title: title.trim(),
      date,
      endDate: isMultiDay ? endDate : null,
      isMultiDay,
      startTime,
      endTime,
      priority,
      color: color || null,
      note: note.trim() || null,
      done: initialData?.done || false,
      targetCalendar: targetCalendar || null,
      googleEventId: initialData?.googleEventId || null,
      exportedToCalendar: initialData?.exportedToCalendar || null,
      contactRef: contactRef || initialData?.contactRef || null,  // Kontakt-Verknüpfung beibehalten
    });
    
    setTitle('');
    setNote('');
    setOverlapError('');
    onClose();
  };

  const handleDelete = () => {
    if (initialData?.id) {
      onDelete(initialData.id);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[300] p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-600" />
            {initialData ? 'Termin bearbeiten' : 'Neuer Termin'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {showDeleteConfirm ? (
          <div className="text-center py-4">
            <p className="text-slate-700 mb-2">Termin wirklich löschen?</p>
            <p className="text-sm text-slate-500 mb-4">Der Termin wird aus allen Ansichten (Jahr, Monat, Woche) entfernt.</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
                <Trash2 className="w-4 h-4 mr-2" /> Löschen
              </Button>
              <Button onClick={() => setShowDeleteConfirm(false)} variant="outline">
                Abbrechen
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            {/* Color dot at top right - click to expand */}
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 mb-1">Was machst du?</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="z.B. Meeting, Sport, Lernen..."
                  className="w-full p-2 sm:p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  autoFocus
                />
              </div>
              {/* Color picker button */}
              <div className="relative pt-6">
                <button
                  type="button"
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  className={`w-10 h-10 rounded-full ${currentColorData.bg} shadow-md hover:scale-110 transition-all ring-2 ring-white`}
                  title="Farbe wählen"
                />
                {/* Color picker dropdown - positioned to the left for mobile compatibility */}
                {showColorPicker && (
                  <div className="absolute right-0 sm:right-0 top-14 bg-white rounded-xl shadow-2xl border border-slate-200 p-3 z-[100]" style={{ minWidth: '150px', transform: 'translateX(0)' }}>
                    <p className="text-xs text-slate-500 mb-2 text-center">Farbe wählen</p>
                    <div className="grid grid-cols-4 gap-3">
                      {COLOR_KEYS.map((colorKey) => {
                        const colorData = APPOINTMENT_COLORS[colorKey];
                        const isSelected = currentColorKey === colorKey;
                        return (
                          <button
                            key={colorKey}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setColor(colorKey);
                              setShowColorPicker(false);
                            }}
                            className={`w-7 h-7 rounded-full transition-all ${colorData.bg} ${
                              isSelected ? 'ring-2 ring-offset-2 ring-slate-800 scale-110' : 'hover:scale-110 active:scale-95'
                            }`}
                            style={{ touchAction: 'manipulation' }}
                            title={colorKey.charAt(0).toUpperCase() + colorKey.slice(1)}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Datum</label>
              <input
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  if (!isMultiDay || endDate < e.target.value) {
                    setEndDate(e.target.value);
                  }
                }}
                className="w-full p-2 sm:p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            {/* Multi-day checkbox */}
            {!hideMultiDay && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="multiDay"
                checked={isMultiDay}
                onChange={(e) => {
                  setIsMultiDay(e.target.checked);
                  if (e.target.checked) {
                    // Set end date to next day automatically
                    const nextDay = new Date(date);
                    nextDay.setDate(nextDay.getDate() + 1);
                    setEndDate(getLocalDateStr(nextDay));
                  } else {
                    setEndDate(date);
                  }
                }}
                className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
              />
              <label htmlFor="multiDay" className="text-sm font-medium text-slate-700">Mehrere Tage</label>
            </div>
            )}

            {/* End date for multi-day events */}
            {!hideMultiDay && isMultiDay && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Bis Datum</label>
                <input
                  type="date"
                  value={endDate}
                  min={(() => {
                    const nextDay = new Date(date);
                    nextDay.setDate(nextDay.getDate() + 1);
                    return getLocalDateStr(nextDay);
                  })()}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full p-2 sm:p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Von</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => handleStartTimeChange(e.target.value)}
                  className="w-full p-2 sm:p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Bis</label>
                <input
                  type="time"
                  value={endTime}
                  min={getMinEndTime()}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full p-2 sm:p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
                {!isMultiDay && endTime <= startTime && (
                  <p className="text-red-500 text-xs mt-1">Endzeit muss nach Startzeit sein</p>
                )}
              </div>
            </div>

            {overlapError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm font-medium">⚠️ Terminkonflikt!</p>
                <p className="text-red-500 text-xs mt-1">{overlapError}</p>
              </div>
            )}

            {/* Priority Slider */}
            <div>
              <label className="text-sm font-medium text-slate-700">Priorität: {priority}</label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-green-600 text-xs">Niedrig</span>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={priority}
                  onChange={(e) => setPriority(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                />
                <span className="text-red-600 text-xs">Hoch</span>
              </div>
            </div>

            {/* Notes Field */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notizen (optional)</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onFocus={() => setNotesFocused(true)}
                onBlur={() => setNotesFocused(false)}
                placeholder="Zusätzliche Informationen zum Termin..."
                className="w-full p-2 sm:p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm resize-none transition-all duration-200"
                rows={notesFocused ? 3 : 1}
              />
            </div>

            {/* Kontakt verknüpfen */}
            <div className="relative" ref={contactSearchRef}>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700 flex items-center gap-1 whitespace-nowrap flex-shrink-0">
                  <User className="w-3.5 h-3.5" />
                  Kontakt:
                </label>
                {contactRef?.name ? (
                  <div className="flex items-center gap-1.5 px-2 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg flex-1 min-w-0">
                    <span className="text-sm text-emerald-700 font-medium truncate flex-1">{contactRef.name}</span>
                    <button type="button" onClick={() => { setContactRef(null); setContactSearch(''); }} className="text-slate-400 hover:text-red-500 flex-shrink-0">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="relative flex-1">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input
                      type="text"
                      value={contactSearch}
                      onChange={(e) => { setContactSearch(e.target.value); setContactSearchOpen(true); }}
                      onFocus={() => setContactSearchOpen(true)}
                      placeholder="Name suchen..."
                      className="w-full pl-7 pr-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    />
                    {contactSearchOpen && contactResults.length > 0 && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-36 overflow-y-auto">
                        {contactResults.map(c => (
                          <button type="button" key={c.id}
                            onClick={() => { setContactRef({ id: c.id, typ: c.typ || 'teampartner', name: c.name }); setContactSearch(c.name); setContactSearchOpen(false); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 flex items-center gap-2"
                          >
                            <span>{c.typ === 'teampartner' ? '👤' : c.typ === 'kunde' ? '🛒' : '❄️'}</span>
                            <span>{c.name}</span>
                            {c.typ && <span className="text-xs text-slate-400 ml-auto capitalize">{c.typ}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Google Calendar Selection - only show if connected */}
            {calendars && calendars.length > 0 && !initialData?.isGoogleEvent && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  📤 Zu Google Kalender hinzufügen
                </label>
                <select
                  value={targetCalendar}
                  onChange={(e) => setTargetCalendar(e.target.value)}
                  className="w-full p-2 sm:p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                >
                  <option value="">Nicht zu Google hinzufügen</option>
                  {calendars.filter(c => c.accessRole === 'owner' || c.accessRole === 'writer').map(cal => (
                    <option key={cal.id} value={cal.id}>{cal.summary}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">
                <Check className="w-4 h-4 mr-2" /> Speichern
              </Button>
              <Button 
                type="button" 
                onClick={onClose}
                variant="outline"
                className="flex-1"
              >
                Abbrechen
              </Button>
              {initialData && (
                <Button 
                  type="button" 
                  onClick={() => setShowDeleteConfirm(true)}
                  className="bg-red-100 hover:bg-red-200 text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>

            {/* Erledigt button - nur bei bestehenden Terminen (mit ID) und aktuellem Tag */}
            {initialData?.id && isToday && onToggleDone && (
              <button
                type="button"
                onClick={() => { onToggleDone(initialData.id); onClose(); }}
                className={`w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                  initialData.done
                    ? 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                }`}
              >
                <Check className="w-4 h-4" />
                {initialData.done ? 'Als offen markieren' : 'Erledigt'}
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
